import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Save, Truck } from "lucide-react";
import api, { formatPrice, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const EMPTY_ZONE = { name: "", regions: [], price: 0, free_from: null, estimated_days: "" };
const EMPTY = { name: "", code: "", logo: "", tracking_url_template: "", enabled: true, zones: [] };

export default function AdminCarriers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/carriers").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      const payload = {
        ...data,
        zones: (data.zones || []).map(z => ({
          ...z,
          price: parseFloat(z.price) || 0,
          free_from: z.free_from === "" || z.free_from === null ? null : parseFloat(z.free_from),
          regions: Array.isArray(z.regions) ? z.regions : (z.regions || "").split(",").map(r => r.trim()).filter(Boolean),
        })),
      };
      if (data.id) {
        await api.put(`/admin/carriers/${data.id}`, payload);
        toast.success("Transportista guardado");
      } else {
        await api.post("/admin/carriers", payload);
        toast.success("Transportista creado");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este transportista?")) return;
    await api.delete(`/admin/carriers/${id}`);
    toast.success("Eliminado");
    load();
  };

  return (
    <div data-testid="admin-carriers">
      <div className="flex justify-between items-start mb-8">
        <div>
          <p className="overline mb-3">Logística</p>
          <h1 className="font-heading text-4xl text-ivory">Transportistas</h1>
          <p className="text-sm text-ivory/60 mt-3 max-w-2xl">Gestiona los transportistas y configura las tarifas de envío por zona geográfica.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-gold !py-2 !px-4 flex items-center gap-2" data-testid="new-carrier-btn">
          <Plus size={14} /> Nuevo transportista
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {items.map(c => (
          <div key={c.id} className="bg-graphite border border-smoke p-6" data-testid={`carrier-${c.code || c.id}`}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-smoke border border-smoke flex items-center justify-center">
                  {c.logo ? <img src={c.logo} alt="" className="w-full h-full object-contain" /> : <Truck size={20} className="text-gold" />}
                </div>
                <div>
                  <p className="font-heading text-xl text-ivory">{c.name}</p>
                  <p className="text-xs text-ivory/40 uppercase tracking-wider">{c.code}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {c.enabled ? <span className="text-[10px] uppercase tracking-[0.15em] text-gold border border-gold px-2 py-1">Activo</span> : <span className="text-[10px] uppercase tracking-[0.15em] text-ivory/40 border border-smoke px-2 py-1">Inactivo</span>}
                <button onClick={() => setEditing(c)} className="text-ivory/60 hover:text-gold" data-testid={`edit-carrier-${c.id}`}><Edit size={14} /></button>
                <button onClick={() => del(c.id)} className="text-ivory/60 hover:text-rose"><Trash2 size={14} /></button>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-ivory/40">Zonas ({(c.zones || []).length})</p>
              {(c.zones || []).map((z, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-smoke/50">
                  <div>
                    <p className="text-sm text-ivory">{z.name}</p>
                    <p className="text-xs text-ivory/40">{(z.regions || []).join(", ") || "—"} {z.estimated_days && `· ${z.estimated_days} días`}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gold price-card" style={{ fontSize: "16px" }}>{formatPrice(z.price)}</p>
                    {z.free_from && <p className="text-[10px] text-ivory/40">Gratis &gt; {formatPrice(z.free_from)}</p>}
                  </div>
                </div>
              ))}
              {(c.zones || []).length === 0 && <p className="text-xs text-ivory/40">Sin zonas configuradas</p>}
            </div>
          </div>
        ))}
      </div>

      {editing && <CarrierForm initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function CarrierForm({ initial, onClose, onSave }) {
  const [d, setD] = useState({
    ...initial,
    zones: (initial.zones || []).map(z => ({
      ...z,
      regions: Array.isArray(z.regions) ? z.regions.join(", ") : (z.regions || ""),
    })),
  });
  const ch = (k, v) => setD({ ...d, [k]: v });

  const addZone = () => setD({ ...d, zones: [...d.zones, { ...EMPTY_ZONE, regions: "" }] });
  const updZone = (i, k, v) => {
    const zones = [...d.zones];
    zones[i] = { ...zones[i], [k]: v };
    setD({ ...d, zones });
  };
  const delZone = (i) => setD({ ...d, zones: d.zones.filter((_, j) => j !== i) });

  return (
    <div className="fixed inset-0 z-[100] bg-carbon/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-graphite border border-smoke max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-smoke">
          <h2 className="font-heading text-2xl text-ivory">{d.id ? "Editar transportista" : "Nuevo transportista"}</h2>
          <button onClick={onClose} className="text-ivory hover:text-gold"><X size={20} /></button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(d); }} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="luxe-label">Nombre</label><input data-testid="carrier-name" required value={d.name} onChange={e => ch("name", e.target.value)} className="luxe-input" /></div>
            <div><label className="luxe-label">Código (slug interno)</label><input value={d.code || ""} onChange={e => ch("code", e.target.value)} className="luxe-input" placeholder="ej: correos, seur..." /></div>
            <div className="col-span-2"><label className="luxe-label">URL logo (opcional)</label><input value={d.logo || ""} onChange={e => ch("logo", e.target.value)} className="luxe-input" /></div>
            <div className="col-span-2">
              <label className="luxe-label">URL de tracking ({"{tracking}"} se reemplaza por el nº)</label>
              <input value={d.tracking_url_template || ""} onChange={e => ch("tracking_url_template", e.target.value)} className="luxe-input" placeholder="https://transportista.com/track/{tracking}" />
            </div>
            <label className="flex items-center gap-2 text-ivory col-span-2">
              <input type="checkbox" checked={d.enabled} onChange={e => ch("enabled", e.target.checked)} className="accent-gold" /> Activo
            </label>
          </div>

          <div className="pt-4 border-t border-smoke">
            <div className="flex justify-between items-center mb-4">
              <label className="luxe-label !mb-0">Zonas de envío</label>
              <button type="button" onClick={addZone} className="text-xs text-gold uppercase tracking-wider flex items-center gap-1" data-testid="add-zone"><Plus size={12} /> Añadir zona</button>
            </div>
            <div className="space-y-4">
              {d.zones.map((z, i) => (
                <div key={i} className="bg-carbon border border-smoke p-4 space-y-3" data-testid={`zone-${i}`}>
                  <div className="flex justify-between items-center">
                    <span className="overline" style={{ fontSize: "12px" }}>Zona {i + 1}</span>
                    <button type="button" onClick={() => delZone(i)} className="text-ivory/40 hover:text-rose"><Trash2 size={14} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="luxe-label">Nombre zona</label><input data-testid={`zone-name-${i}`} required value={z.name} onChange={e => updZone(i, "name", e.target.value)} className="luxe-input" placeholder="Península / Baleares / UE..." /></div>
                    <div><label className="luxe-label">Días estimados</label><input value={z.estimated_days || ""} onChange={e => updZone(i, "estimated_days", e.target.value)} className="luxe-input" placeholder="2-3" /></div>
                    <div className="col-span-2">
                      <label className="luxe-label">Regiones/países (separados por coma)</label>
                      <input value={z.regions} onChange={e => updZone(i, "regions", e.target.value)} className="luxe-input" placeholder="España peninsular, Andorra..." />
                    </div>
                    <div><label className="luxe-label">Precio (€)</label><input data-testid={`zone-price-${i}`} type="number" step="0.01" required value={z.price} onChange={e => updZone(i, "price", e.target.value)} className="luxe-input" /></div>
                    <div><label className="luxe-label">Envío gratis desde (€) — opcional</label><input type="number" step="0.01" value={z.free_from ?? ""} onChange={e => updZone(i, "free_from", e.target.value)} className="luxe-input" placeholder="50" /></div>
                  </div>
                </div>
              ))}
              {d.zones.length === 0 && <p className="text-xs text-ivory/40">Sin zonas. Añade al menos una.</p>}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-smoke">
            <button type="submit" className="btn-gold flex items-center gap-2" data-testid="carrier-save"><Save size={14} /> Guardar</button>
            <button type="button" onClick={onClose} className="btn-outline-gold">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}
